import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Toaster, toast } from "react-hot-toast";
import Select from "react-select";
import {
  fetchAllowedMenus,
  fetchStateData,
  fetchDistrictData,
  fetchCityData,
  fetchMeadiaSourceData,
  fetchAllSubMediaSource,
  fetchUserData,
  User,
  updateLead,
  GetCountryCodes,    // ✅ add
  CountryCodeDto,     // ✅ add
} from "../Services/APIService";

interface MenuItem {
  menuID: number;
  menuName: string;
  menuUrl: string;
  icon: string;
  subMenuList?: [];
}

interface State {
  stateId: number;
  stateName: string;
  isActive: boolean;
}

interface District {
  districtId: number;
  districtName: string;
  stateId: number;
  stateName: string;
  isActive: boolean;
}

interface City {
  cityId: number;
  cityName: string;
  districtId: number;
  districtName: string;
  stateId: number;
  stateName: string;
  isActive: boolean;
}

interface MediaSource {
  sourceId: number;
  sourceName: string;
  isActive: boolean;
}

interface SubMediaSource {
  mediaSourceID: number;
  name: string;
  id: number;
  isActive: boolean;
}

interface Lead {
  leadId: number;
  firstName: string;
  lastName: string;
  gender: string;
  dob: string;
  dom: string;
  mobile: string;
  email: string;
  address: string;
  stateId: number;
  districtId: number;
  cityId: number;
  zipCode: number | string;
  sourceId: number;
  subSourceId: number;
  currentStatus: number;
  incomeFrom: number | string;
  incomeTo: number | string;
  initialAssignedUser: number;
  budgetMin?: number | string;
  budgetMax?: number | string;
  currentAssignedUser: number;
  tenantId: number;
  modifiedBy?: number;
  assignType: string;

  countryCode: string;
  alternateMobile: string;
  alternateMobileCountryCode: string;
}

interface ValidationErrors {
  firstName?: string;
  mobile?: string;
  email?: string;
  sourceId?: string;
  subSourceId?: string;
  assignType?: string;
  currentAssignedUser?: string;
  general?: string;
  alternateMobile?: string;

}

interface UpdateLeadModalProps {
  isOpen: boolean;
  onClose: () => void;
  leadsData: Lead;
  onSubmit: (updatedLead: Lead) => Promise<void>;
  onSuccess?: () => void;
}

interface StateOption { value: number; label: string; }
interface DistrictOption { value: number; label: string; }
interface CityOption { value: number; label: string; }
interface UserOption { value: number; label: string; }
interface MediaSourceOption { value: number; label: string; }
interface SubMediaSourceOption { value: number; label: string; }

const UpdateLeadModal: React.FC<UpdateLeadModalProps> = ({
  isOpen,
  onClose,
  leadsData,
  onSubmit,
  onSuccess,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [states, setStates] = useState<State[]>([]);
  const [allDistricts, setAllDistricts] = useState<District[]>([]);
  const [filteredDistricts, setFilteredDistricts] = useState<District[]>([]);
  const [allCities, setAllCities] = useState<City[]>([]);
  const [filteredCities, setFilteredCities] = useState<City[]>([]);
  const [mediaSource, setMediaSource] = useState<MediaSource[]>([]);
  const [allSubMediaSource, setAllSubMediaSource] = useState<SubMediaSource[]>([]);
  const [filteredSubMediaSource, setFilteredSubMediaSource] = useState<SubMediaSource[]>([]);
  const [activeTab, setActiveTab] = useState("home");
  const [leadData, setLeadData] = useState<Lead | null>(null);
  const [assignedUser, setAssignedUser] = useState<User[]>([]);
  const [assignType, setAssignType] = useState("assign");

  // Select options
  const [stateOptions, setStateOptions] = useState<StateOption[]>([]);
  const [districtOptions, setDistrictOptions] = useState<DistrictOption[]>([]);
  const [cityOptions, setCityOptions] = useState<CityOption[]>([]);
  const [mediaSourceOptions, setMediaSourceOptions] = useState<MediaSourceOption[]>([]);
  const [subMediaSourceOptions, setSubMediaSourceOptions] = useState<SubMediaSourceOption[]>([]);
  const [assignUserOptions, setAssignUserOptions] = useState<UserOption[]>([]);
  const [countryCodes, setCountryCodes] = useState<CountryCodeDto[]>([]); // ✅ add

  const requiredField = <span className="text-danger ms-1">*</span>;

  // ── Shared props for ALL react-select dropdowns ──────────────
  // Renders the menu into <body> so modal overflow/z-index never clips it
  const selectPortalProps = {
    menuPortalTarget: document.body,
    menuPosition: "fixed" as const,
    styles: { menuPortal: (base: object) => ({ ...base, zIndex: 9999 }) },
  };

  // ── Fetch all master data on mount ──────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [
          stateData,
          districtData,
          cityData,
          mediaSourceData,
          subMediaSourceData,
          assignUserData,
          ccData,              // ✅ add
        ] = await Promise.all([
          fetchStateData(),
          fetchDistrictData(),
          fetchCityData(),
          fetchMeadiaSourceData(),
          fetchAllSubMediaSource(),
          fetchUserData(),
          GetCountryCodes(),   // ✅ add
        ]);

        // ✅ Set country codes
        setCountryCodes(ccData);

        const activeStates = stateData.filter((s) => s.isActive);
        const activeDistricts = districtData.filter((d) => d.isActive);
        const activeCities = cityData.filter((c) => c.isActive);
        const activeSources = mediaSourceData.filter((s) => s.isActive);
        const activeSubSources = subMediaSourceData.filter((s) => s.isActive);
        const activeUsers = assignUserData.filter((u) => u.isActive);

        setStates(activeStates);
        setStateOptions(activeStates.map((s) => ({ value: s.stateId, label: s.stateName })));

        setAllDistricts(activeDistricts);
        setAllCities(activeCities);

        setMediaSource(activeSources);
        setMediaSourceOptions(activeSources.map((s) => ({ value: s.sourceId, label: s.sourceName })));

        setAllSubMediaSource(activeSubSources);

        setAssignedUser(activeUsers);
        setAssignUserOptions(activeUsers.map((u) => ({ value: u.userId, label: u.userName })));
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load required data");
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  // ── Initialize lead form from prop ──────────────────────────
  const formatDateForInput = (dateStr: string | null | undefined): string => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = `0${date.getMonth() + 1}`.slice(-2);
    const day = `0${date.getDate()}`.slice(-2);
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    if (leadsData) {
      const genderMap: { [key: string]: string } = {
        M: "M", F: "F", O: "O", Male: "M", Female: "F", Other: "O",
      };
      setLeadData({
        ...leadsData,
        gender: genderMap[leadsData.gender] || "",
        dob: formatDateForInput(leadsData.dob),
        dom: formatDateForInput(leadsData.dom),
        zipCode: leadsData.zipCode || "",
        incomeFrom: leadsData.incomeFrom || "",
        incomeTo: leadsData.incomeTo || "",
        budgetMin: leadsData?.budgetMin || "",
        budgetMax: leadsData?.budgetMax || "",
        mobile: leadsData.mobile ? leadsData.mobile.slice(-10) : "",

        countryCode: leadsData.countryCode || "+91",
        // ✅ Strip country code prefix from alternate mobile too
        alternateMobile: leadsData.alternateMobile
          ? leadsData.alternateMobile.slice(-10)
          : "",
        alternateMobileCountryCode: leadsData.alternateMobileCountryCode || "+91",
      });
    }
  }, [leadsData]);
  // ── Cascading: State → District ─────────────────────────────
  useEffect(() => {
    if (leadData?.stateId) {
      const filtered = allDistricts.filter(
        (d) => d.stateId === leadData.stateId && d.isActive
      );
      setFilteredDistricts(filtered);
      setDistrictOptions(filtered.map((d) => ({ value: d.districtId, label: d.districtName })));
    } else {
      setFilteredDistricts([]);
      setDistrictOptions([]);
    }
  }, [leadData?.stateId, allDistricts]);

  // ── Cascading: District → City ──────────────────────────────
  useEffect(() => {
    if (leadData?.districtId) {
      const filtered = allCities.filter(
        (c) => c.districtId === leadData.districtId && c.isActive
      );
      setFilteredCities(filtered);
      setCityOptions(filtered.map((c) => ({ value: c.cityId, label: c.cityName })));
    } else {
      setFilteredCities([]);
      setCityOptions([]);
    }
  }, [leadData?.districtId, allCities]);

  // ── Cascading: Source → Sub Source ──────────────────────────
  useEffect(() => {
    if (leadData?.sourceId && allSubMediaSource.length > 0) {
      const filtered = allSubMediaSource.filter(
        (s) => s.mediaSourceID === leadData.sourceId && s.isActive
      );
      setFilteredSubMediaSource(filtered);
      setSubMediaSourceOptions(filtered.map((s) => ({ value: s.id, label: s.name })));

      const isStillValid = filtered.some((s) => s.id === leadData.subSourceId);
      if (!isStillValid && leadData.subSourceId) {
        setLeadData((prev) => prev ? { ...prev, subSourceId: 0 } : null);
      }
    } else {
      setFilteredSubMediaSource([]);
      setSubMediaSourceOptions([]);
    }
  }, [leadData?.sourceId, allSubMediaSource]);

  // ── Assign type detection ────────────────────────────────────
  useEffect(() => {
    if (leadData) {
      const currentUserId = parseInt(localStorage.getItem("UserId") ?? "0");
      const assignedUserId = leadData.currentAssignedUser;
      if (currentUserId === assignedUserId && currentUserId !== 0) {
        setAssignType("self");
        setLeadData((prev) => prev ? { ...prev, assignType: "self" } : null);
      } else {
        setAssignType("assign");
        setLeadData((prev) => prev ? { ...prev, assignType: "assign" } : null);
      }
    }
  }, [leadData?.initialAssignedUser]);

  // ── Validation ───────────────────────────────────────────────
  const validateEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validateMobile = (mobile: string) => /^\d+$/.test(mobile);

  const validateHomeTab = (): boolean => {
    if (!leadData) { setErrors({ general: "Lead data is not available" }); return false; }
    const newErrors: ValidationErrors = {};
    let isValid = true;

    if (!leadData.firstName.trim()) { newErrors.firstName = "First name is required"; isValid = false; }
    if (!leadData.mobile || String(leadData.mobile).trim() === "") { newErrors.mobile = "Mobile number is required"; isValid = false; }
    else if (!validateMobile(leadData.mobile)) { newErrors.mobile = "Mobile should contain only digits"; isValid = false; }
    if (leadData.email?.trim() && !validateEmail(leadData.email)) { newErrors.email = "Invalid email format"; isValid = false; }
    if (!leadData.sourceId) { newErrors.sourceId = "Please select a Source"; isValid = false; }
    if (!leadData.assignType) { newErrors.assignType = "Please select an Assign Type"; isValid = false; }
    if (leadData.assignType === "assign" && !leadData.currentAssignedUser) { newErrors.currentAssignedUser = "Please select a user to assign"; isValid = false; }

    if (
      leadData.alternateMobile?.trim() &&
      !/^\d+$/.test(leadData.alternateMobile)
    ) {
      newErrors.alternateMobile = "Alternate mobile should contain only digits";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleNextClick = () => { if (validateHomeTab()) { setActiveTab("files"); } };

  // ── Input change handler ─────────────────────────────────────
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    const numberFields = [
      "zipCode", "incomeFrom", "budgetMax", "budgetMin",
      "incomeTo", "stateId", "districtId", "cityId",
      "sourceId", "subSourceId", "currentAssignedUser",
    ];

    if (name === "assignType") {
      setAssignType(value);
      if (value === "self") {
        const currentUserId = parseInt(localStorage.getItem("UserId") ?? "0");
        setLeadData((prev) =>
          prev ? { ...prev, assignType: value, initialAssignedUser: currentUserId, currentAssignedUser: currentUserId } : null
        );
      } else {
        setLeadData((prev) => prev ? { ...prev, assignType: value } : null);
      }
    } else {
      const isNumeric = numberFields.includes(name);
      if (isNumeric && value !== "" && !/^[0-9]*$/.test(value)) return;
      const parsedValue = isNumeric ? (value === "" ? "" : parseInt(value, 10)) : value;
      setLeadData((prev) => prev ? { ...prev, [name]: parsedValue } : null);
    }

    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  // ── Select change handlers ───────────────────────────────────
  const handleStateChange = (option: StateOption | null) => {
    setLeadData((prev) => prev ? { ...prev, stateId: option?.value ?? 0, districtId: 0, cityId: 0 } : null);
  };

  const handleDistrictChange = (option: DistrictOption | null) => {
    setLeadData((prev) => prev ? { ...prev, districtId: option?.value ?? 0, cityId: 0 } : null);
  };

  const handleCityChange = (option: CityOption | null) => {
    setLeadData((prev) => prev ? { ...prev, cityId: option?.value ?? 0 } : null);
  };

  const handleMediaSourceChange = (option: MediaSourceOption | null) => {
    setLeadData((prev) => prev ? { ...prev, sourceId: option?.value ?? 0, subSourceId: 0 } : null);
    setErrors((prev) => ({ ...prev, sourceId: undefined }));
  };

  const handleSubMediaSourceChange = (option: SubMediaSourceOption | null) => {
    setLeadData((prev) => prev ? { ...prev, subSourceId: option?.value ?? 0 } : null);
    setErrors((prev) => ({ ...prev, subSourceId: undefined }));
  };

  const handleAssignUserChange = (option: UserOption | null) => {
    setLeadData((prev) => prev ? { ...prev, currentAssignedUser: option?.value ?? 0 } : null);
    setErrors((prev) => ({ ...prev, currentAssignedUser: undefined }));
  };

  // ── Submit ───────────────────────────────────────────────────
  const toNullableDate = (value?: string): string | null =>
    value && value.trim() !== "" ? value : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadData) { toast.error("Lead data is not available"); return; }
    if (!validateHomeTab()) return;

    try {
      const tenantId = localStorage.getItem("tenantId");
      const accessToken = localStorage.getItem("authToken");
      const currentUserId = localStorage.getItem("UserId");
      const isImpersonating = localStorage.getItem("isImpersonating") === "true";
      const originalSessionRaw = localStorage.getItem("originalSession");

      if (!tenantId || !accessToken) throw new Error("Missing required credentials");

      let modifiedByUserId: number;
      if (isImpersonating && originalSessionRaw) {
        modifiedByUserId = parseInt(JSON.parse(originalSessionRaw).UserId);
      } else {
        if (!currentUserId) throw new Error("User ID not found");
        modifiedByUserId = parseInt(currentUserId);
      }

      const updateData = {
        leadId: leadData.leadId,
        firstName: leadData.firstName,
        mobile: leadData.mobile,
        email: leadData.email,
        sourceId: leadData.sourceId,
        subSourceId: leadData.subSourceId || null,   // ← send null instead of 0
        assignType: leadData.assignType,
        currentAssignedUser: leadData.currentAssignedUser,
        modifiedBy: modifiedByUserId,
        tenantId: parseInt(tenantId),
        lastName: leadData.lastName || null,
        gender: leadData.gender || null,
        dob: toNullableDate(leadData.dob),
        dom: toNullableDate(leadData.dom),
        address: leadData.address || null,
        stateId: leadData.stateId || null,
        districtId: leadData.districtId || null,
        cityId: leadData.cityId || null,
        zipCode: Number(leadData.zipCode) || null,
        currentStatus: leadData.currentStatus || null,
        incomeFrom: Number(leadData.incomeFrom) || null,
        incomeTo: Number(leadData.incomeTo) || null,
        budgetMin: Number(leadData.budgetMin) || null,
        budgetMax: Number(leadData.budgetMax) || null,
        initialAssignedUser: leadData.initialAssignedUser || null,

        countryCode: leadData.countryCode || null,
        alternateMobile: leadData.alternateMobile || null,
        alternateMobileCountryCode: leadData.alternateMobileCountryCode || null,
      };

      const result = await updateLead(updateData);

      if (result.isUpdated) {
        toast.success("Lead updated successfully");
        onClose();
        navigate("/leadview", { state: { refresh: true }, replace: true });
        if (onSuccess) onSuccess();
      } else {
        toast.error(result.message || "Update failed");
      }
    } catch (error: any) {
      console.error("Error updating lead:", error?.response?.data || error);
      toast.error(error?.response?.data?.message || "An error occurred while updating the lead");
    }
  };

  const handleTabClick = (tabId: string) => setActiveTab(tabId);

  const handleClose = () => {
    setErrors({});
    setActiveTab("home");
    onClose();
  };

  // ── Option getters ───────────────────────────────────────────
  const getOpt = <T extends { value: number }>(options: T[], value: number | undefined): T | null =>
    options.find((o) => o.value === value) ?? null;


  const countryCodeOptions = countryCodes.map((cc) => ({
    value: cc.value,          // e.g. "+91"
    label: cc.value,          // ✅ show only "+91" in the selected box
    fullLabel: cc.fullLabel,  // e.g. "India (+91)" — used only in dropdown list
  }));

  const filterCountryCode = (option: any, inputValue: string) => {
    const search = inputValue.toLowerCase();
    return (
      option.data.fullLabel?.toLowerCase().includes(search) ||
      option.data.value?.toLowerCase().includes(search)
    );
  };

  const countrySelectStyles = {
    control: (base: any) => ({
      ...base,
      minHeight: "42px",
      height: "42px",
      fontSize: "13px",
      cursor: "pointer",
    }),
    valueContainer: (base: any) => ({
      ...base,
      padding: "0 6px",
    }),
    input: (base: any) => ({
      ...base,
      fontSize: "13px",
      margin: 0,
      padding: 0,
    }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (base: any) => ({
      ...base,
      padding: "0 4px",
    }),
    menu: (base: any) => ({
      ...base,
      width: "220px",   // ✅ wider so full label visible in dropdown
      zIndex: 9999,
    }),
    menuList: (base: any) => ({
      ...base,
      maxHeight: "200px",
    }),
    option: (base: any, state: any) => ({
      ...base,
      fontSize: "13px",
      padding: "8px 12px",
      backgroundColor: state.isSelected
        ? "#9b2c6e"
        : state.isFocused
          ? "#f5ebf0"
          : "white",
      color: state.isSelected ? "white" : "#333",
    }),
    noOptionsMessage: (base: any) => ({
      ...base,
      fontSize: "13px",
    }),
    // ✅ Format the selected value to show only dial code
    singleValue: (base: any) => ({
      ...base,
      fontSize: "13px",
    }),
  };

  if (isLoading || !leadData) return <div className="p-4 text-center">Loading lead data...</div>;
  if (!isOpen) return null;

  return (
    <>
      <div className={`modal-backdrop-custom ${isOpen ? "open" : "closed"}`} onClick={handleClose} />
      <div className={`modal-custom ${isOpen ? "open" : "closed"}`}>
        <div className="modal-content" style={{ width: "580px", maxWidth: "95vw" }}>
          <div className="modal-header-custom">
            <h5 className="modal-title-custom">Edit Lead</h5>
            <button type="button" className="btn-close-custom" onClick={handleClose}>
              <span className="icon-close"></span>
            </button>
          </div>

          {/* Tabs */}
          <ul className="nav nav-tabs pop-up-tabHeader" style={{ borderBottom: "1px solid rgb(245,235,240)" }}>
            <li className="nav-item">
              <button
                className={`nav-link card-title tab-btn pop-up-tabHeaderbtn ${activeTab === "home" ? "active" : ""}`}
                onClick={() => handleTabClick("home")}
                type="button"
              >Lead Details</button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link card-title tab-btn pop-up-tabHeaderbtn ${activeTab === "files" ? "active" : ""}`}
                onClick={() => handleTabClick("files")}
                type="button"
              >Other Details</button>
            </li>
          </ul>

          <div className="modal-body-custom">
            <div className="card pop-up-card">
              <div className="tab-content card-body p-0">

                {/* ── Tab 1 — Lead Details ── */}
                <div className={`tab-pane fade ${activeTab === "home" ? "show active" : ""}`}>
                  <form onSubmit={handleSubmit}>
                    <div className="row">

                      {/* First Name */}
                      <div className="col-12 col-lg-6 mb-3 input-group-custom">
                        <label className="form-label">First Name{requiredField}</label>
                        <div className="input-with-icon">
                          <span className="input-icon icon-user"></span>
                          <input
                            type="text" name="firstName" placeholder="Enter First Name"
                            value={leadData.firstName} onChange={handleChange}
                            className={`form-control-custom ${errors.firstName ? "is-invalid" : ""}`}
                          />
                          {errors.firstName && <div className="invalid-feedback text-danger">{errors.firstName}</div>}
                        </div>
                      </div>

                      {/* Last Name */}
                      <div className="col-12 col-lg-6 mb-3 input-group-custom">
                        <label className="form-label">Last Name</label>
                        <div className="input-with-icon">
                          <span className="input-icon icon-user"></span>
                          <input
                            type="text" name="lastName" placeholder="Enter Last Name"
                            value={leadData.lastName} onChange={handleChange}
                            className="form-control-custom"
                          />
                        </div>
                      </div>

                      {/* Mobile with Country Code */}
                      <div className="col-12 col-lg-6 mb-3 input-group-custom">
                        <label className="form-label">Mobile{requiredField}</label>

                        <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>

                          {/* Country Code */}
                          <div style={{ width: "85px", flexShrink: 0 }}>
                            <Select
                              options={countryCodeOptions}
                              value={countryCodeOptions.find(opt => opt.value === (leadData.countryCode || "+91"))}
                              onChange={(option) =>
                                setLeadData((prev) => prev
                                  ? { ...prev, countryCode: option ? option.value : "+91" }
                                  : null
                                )
                              }
                              isSearchable
                              isClearable={false}
                              filterOption={filterCountryCode}
                              placeholder="+91"
                              classNamePrefix="select"
                              styles={countrySelectStyles}
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              // ✅ Show full label in dropdown, only code when selected
                              formatOptionLabel={(option: any, { context }) =>
                                context === "menu" ? option.fullLabel : option.value
                              }
                            />
                          </div>

                          {/* Mobile Input — read only */}
                          <div className="input-with-icon flex-grow-1">
                            <span className="input-icon icon-phone"></span>
                            <input
                              type="text"
                              name="mobile"
                              placeholder="Enter Mobile No"
                              value={leadData.mobile}
                              readOnly
                              maxLength={15}
                              minLength={3}
                              style={{ backgroundColor: "#e9ecef", cursor: "not-allowed" }}
                              className={`form-control-custom ${errors.mobile ? "is-invalid" : ""}`}
                            />
                          </div>

                        </div>

                        {errors.mobile && (
                          <div className="text-danger d-block" style={{ fontSize: "12px", marginTop: "4px" }}>
                            {errors.mobile}
                          </div>
                        )}
                      </div>

                      {/* Alternate Mobile with Country Code */}
                      <div className="col-12 col-lg-6 mb-3 input-group-custom">
                        <label className="form-label">Alternate Mobile</label>

                        <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>

                          {/* Alternate Country Code */}
                          <div style={{ width: "85px", flexShrink: 0, pointerEvents: "auto" }}>
                            <Select
                              options={countryCodeOptions}
                              value={countryCodeOptions.find(opt => opt.value === (leadData.alternateMobileCountryCode || "+91"))}
                              onChange={(option) =>
                                setLeadData((prev) => prev
                                  ? { ...prev, alternateMobileCountryCode: option ? option.value : "+91" }
                                  : null
                                )
                              }
                              isSearchable
                              isClearable={false}
                              filterOption={filterCountryCode}
                              placeholder="+91"
                              classNamePrefix="select"
                              styles={{
                                ...countrySelectStyles,
                                control: (base) => ({
                                  ...base,
                                  cursor: "pointer",
                                  pointerEvents: "auto",
                                }),
                                menuPortal: (base) => ({
                                  ...base,
                                  zIndex: 99999,
                                }),
                              }}
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              // ✅ Show full label in dropdown, only code when selected
                              formatOptionLabel={(option: any, { context }) =>
                                context === "menu" ? option.fullLabel : option.value
                              }
                            />
                          </div>

                          {/* Alternate Mobile Input */}
                          <div className="input-with-icon flex-grow-1">
                            <span className="input-icon icon-phone"></span>
                            <input
                              type="text"
                              name="alternateMobile"
                              placeholder="Enter Alternate Mobile No"
                              value={leadData.alternateMobile || ""}
                              maxLength={15}
                              minLength={3}
                              onChange={handleChange}
                              className={`form-control-custom ${errors.alternateMobile ? "is-invalid" : ""}`}
                            />
                          </div>

                        </div>

                        {errors.alternateMobile && (
                          <div className="text-danger d-block" style={{ fontSize: "12px", marginTop: "4px" }}>
                            {errors.alternateMobile}
                          </div>
                        )}
                      </div>

                      {/* Email */}
                      <div className="col-12 col-lg-6 mb-3 input-group-custom">
                        <label className="form-label">Email</label>
                        <div className="input-with-icon">
                          <span className="input-icon icon-email"></span>
                          <input
                            type="text" name="email" placeholder="Enter Email"
                            value={leadData.email} onChange={handleChange}
                            className={`form-control-custom ${errors.email ? "is-invalid" : ""}`}
                          />
                          {errors.email && <div className="invalid-feedback text-danger">{errors.email}</div>}
                        </div>
                      </div>

                      {/* Source ── FIX: menuPortalTarget + menuPosition */}
                      <div className="col-12 col-lg-6 mb-3 input-group-custom">
                        <label className="form-label">Source{requiredField}</label>
                        <Select
                          {...selectPortalProps}
                          options={mediaSourceOptions}
                          value={getOpt(mediaSourceOptions, leadData.sourceId)}
                          onChange={handleMediaSourceChange}
                          className={`basic-single-select ${errors.sourceId ? "is-invalid" : ""}`}
                          classNamePrefix="select"
                          placeholder="Select Source"
                          isSearchable
                          isClearable
                        />
                        {errors.sourceId && (
                          <div className="invalid-feedback text-danger d-block">{errors.sourceId}</div>
                        )}
                      </div>

                      {/* Sub Source ── FIX: menuPortalTarget + menuPosition */}
                      <div className="col-12 col-lg-6 mb-3 input-group-custom">
                        <label className="form-label">Sub Source</label>
                        <Select
                          {...selectPortalProps}
                          options={subMediaSourceOptions}
                          value={getOpt(subMediaSourceOptions, leadData.subSourceId)}
                          onChange={handleSubMediaSourceChange}
                          className="basic-single-select"
                          classNamePrefix="select"
                          placeholder={
                            leadData.sourceId
                              ? subMediaSourceOptions.length > 0
                                ? "Select Sub Source"
                                : "No sub sources available"
                              : "Select source first"
                          }
                          isSearchable
                          isClearable
                          isDisabled={!leadData.sourceId}
                        />
                      </div>

                      {/* Assign Type */}
                      <div className="col-12 col-lg-12 mb-3 input-group-custom">
                        <label className="form-label d-block mb-2">Assign Type{requiredField}</label>
                        <div className="form-check form-check-inline">
                          <input
                            className="form-check-input" type="radio" id="assign"
                            name="assignType" value="assign"
                            checked={assignType === "assign"} onChange={handleChange}
                          />
                          <label className="form-check-label" htmlFor="assign">Assign</label>
                        </div>
                        <div className="form-check form-check-inline">
                          <input
                            className="form-check-input" type="radio" id="self"
                            name="assignType" value="self"
                            checked={assignType === "self"} onChange={handleChange}
                          />
                          <label className="form-check-label" htmlFor="self">Self</label>
                        </div>
                        {errors.assignType && (
                          <div className="invalid-feedback text-danger d-block">{errors.assignType}</div>
                        )}
                      </div>

                      {/* Assign to User ── FIX: menuPortalTarget + menuPosition */}
                      {leadData.assignType === "assign" && (
                        <div className="col-12 col-lg-12 mb-3 input-group-custom">
                          <label className="form-label">Assign to User{requiredField}</label>
                          <Select
                            {...selectPortalProps}
                            options={assignUserOptions}
                            value={getOpt(assignUserOptions, leadData.currentAssignedUser)}
                            onChange={handleAssignUserChange}
                            className={`basic-single-select ${errors.currentAssignedUser ? "is-invalid" : ""}`}
                            classNamePrefix="select"
                            placeholder="Select User"
                            isSearchable
                            isClearable
                          />
                          {errors.currentAssignedUser && (
                            <div className="invalid-feedback text-danger d-block">{errors.currentAssignedUser}</div>
                          )}
                        </div>
                      )}
                    </div>
                  </form>
                </div>

                {/* ── Tab 2 — Other Details ── */}
                <div className={`tab-pane fade ${activeTab === "files" ? "show active" : ""}`}>
                  <form onSubmit={handleSubmit}>
                    <div className="row">

                      <div className="col-12 col-lg-6 mb-3 input-group-custom">
                        <label className="form-label">Budget Min</label>
                        <div className="input-with-icon">
                          <span className="input-icon icon-rupee"></span>
                          <input
                            type="text" name="budgetMin" className="form-control-custom"
                            placeholder="Enter Budget Min" value={leadData.budgetMin} onChange={handleChange}
                          />
                        </div>
                      </div>

                      <div className="col-12 col-lg-6 mb-3 input-group-custom">
                        <label className="form-label">Budget Max</label>
                        <div className="input-with-icon">
                          <span className="input-icon icon-rupee"></span>
                          <input
                            type="text" name="budgetMax" className="form-control-custom"
                            placeholder="Enter Budget Max" value={leadData.budgetMax} onChange={handleChange}
                          />
                        </div>
                      </div>

                      <div className="col-12 col-lg-6 mb-3 input-group-custom">
                        <label className="form-label">Income From</label>
                        <div className="input-with-icon">
                          <span className="input-icon icon-rupee"></span>
                          <input
                            type="text" name="incomeFrom" className="form-control-custom"
                            placeholder="Enter Income From" value={leadData.incomeFrom} onChange={handleChange}
                          />
                        </div>
                      </div>

                      <div className="col-12 col-lg-6 mb-3 input-group-custom">
                        <label className="form-label">Income To</label>
                        <div className="input-with-icon">
                          <span className="input-icon icon-rupee"></span>
                          <input
                            type="text" name="incomeTo" className="form-control-custom"
                            placeholder="Enter Income To" value={leadData.incomeTo} onChange={handleChange}
                          />
                        </div>
                      </div>

                      <div className="col-12 col-lg-12 mb-3 input-group-custom">
                        <label className="form-label">Address</label>
                        <textarea
                          name="address" placeholder="Enter Address"
                          value={leadData.address} onChange={handleChange}
                          className="form-control-custom-textarea"
                        />
                      </div>

                      <div className="col-12 col-lg-6 mb-3 input-group-custom">
                        <label className="form-label">Gender</label>
                        <select
                          name="gender" value={leadData.gender} onChange={handleChange}
                          className="form-select form-control-lg"
                        >
                          <option value="">Select Gender</option>
                          <option value="M">Male</option>
                          <option value="F">Female</option>
                          <option value="O">Other</option>
                        </select>
                      </div>

                      {/* State ── FIX: menuPortalTarget + menuPosition */}
                      <div className="col-12 col-lg-6 mb-3 input-group-custom">
                        <label className="form-label">State</label>
                        <Select
                          {...selectPortalProps}
                          options={stateOptions}
                          value={getOpt(stateOptions, leadData.stateId)}
                          onChange={handleStateChange}
                          classNamePrefix="select"
                          placeholder="Select State"
                          isSearchable
                          isClearable
                        />
                      </div>

                      {/* District ── FIX: menuPortalTarget + menuPosition */}
                      <div className="col-12 col-lg-6 mb-3 input-group-custom">
                        <label className="form-label">District</label>
                        <Select
                          {...selectPortalProps}
                          options={districtOptions}
                          value={getOpt(districtOptions, leadData.districtId)}
                          onChange={handleDistrictChange}
                          classNamePrefix="select"
                          placeholder={leadData.stateId ? "Select District" : "Select state first"}
                          isSearchable
                          isClearable
                          isDisabled={!leadData.stateId}
                        />
                      </div>

                      {/* City ── FIX: menuPortalTarget + menuPosition */}
                      <div className="col-12 col-lg-6 mb-3 input-group-custom">
                        <label className="form-label">City</label>
                        <Select
                          {...selectPortalProps}
                          options={cityOptions}
                          value={getOpt(cityOptions, leadData.cityId)}
                          onChange={handleCityChange}
                          classNamePrefix="select"
                          placeholder={leadData.districtId ? "Select City" : "Select district first"}
                          isSearchable
                          isClearable
                          isDisabled={!leadData.districtId}
                        />
                      </div>

                      <div className="col-12 col-lg-6 mb-3 input-group-custom">
                        <label className="form-label">Zipcode</label>
                        <input
                          type="text" name="zipCode" maxLength={6}
                          className="form-control-custom1 form-control-custom-lg"
                          placeholder="Enter Zipcode" value={leadData.zipCode} onChange={handleChange}
                        />
                      </div>

                      <div className="col-12 col-lg-6 mb-3 input-group-custom">
                        <label className="form-label">Date of Birth</label>
                        <input
                          type="date" name="dob" value={leadData.dob} onChange={handleChange}
                          className="form-control-custom1 form-control-custom-lg"
                        />
                      </div>

                      <div className="col-12 col-lg-6 mb-3 input-group-custom">
                        <label className="form-label">Date of Marriage</label>
                        <input
                          type="date" name="dom" value={leadData.dom} onChange={handleChange}
                          className="form-control-custom1 form-control-custom-lg"
                        />
                      </div>

                    </div>
                  </form>
                </div>

              </div>
            </div>

            {/* Footer */}
            <div className="modal-footer-custom">
              <div>
                {activeTab === "home" ? (
                  <>
                    <button type="button" className="btn-custom btn-cancel" onClick={handleClose}>Cancel</button>
                    <button type="button" className="btn-custom btn-primary-custom" onClick={handleNextClick}>Next</button>
                  </>
                ) : (
                  <>
                    <button type="button" className="btn-custom btn-cancel" onClick={handleClose}>Cancel</button>
                    <button type="button" className="btn-custom btn-primary-custom" onClick={handleSubmit}>Update Lead</button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Toaster />
    </>
  );
};

export default UpdateLeadModal;